import { AxiosRequestConfig } from 'axios';
import instance from 'api';
import { IApplicationModel, IDefectModel, IPositionModel, IRemoteAppeal } from 'interfaces';
import {
  ICapexConstructive,
  ICapexCriticalLevel,
  ICountApplication,
  ICriticalLevel,
  INewApplication,
  IPartner,
  IPhoneInfo,
  IPreviousApplication,
  IRealEstateDefectType,
  IRealEstateRoom,
  IRoomDiscount,
  IServiceType,
  ISmartRemont,
  IUnit,
} from './interfaces';

export const getRealEstates = (
  data: {
    onlyFromMatrix?: boolean;
    onlyWithKeytransferDate?: boolean;
  } = {},
) => {
  return instance()
    .post('/dictionary/realEstates', data)
    .then((response) => response.data);
};

export const getRealEstatesShort = (
  data: {
    areaId?: number;
    includeDeleted?: boolean;
    includeUnserved?: boolean;
    name?: string;
    onlyFromMatrix?: boolean;
    onlyWithKeytransferDate?: boolean;
    onlyWithServiceOrg?: boolean;
    isNoneService?: boolean;
    realEstateGuid?: string;
    searchWithCity?: boolean;
    nameWithCity?: boolean;
    isAccident?: boolean;
  } = {},
) => {
  return instance()
    .post('/dictionary/realEstatesShort', data)
    .then((response) => response.data);
};

export const getRealEstate = (guid: string) => {
  return instance()
    .post('/dictionary/realEstate', { guid })
    .then((response) => response.data);
};

export const getDefectsByPositionShort = () => {
  return instance()
    .post('/dictionary/defectsByPositionShort', {})
    .then((response) => response.data);
};

export const getDefectsFiltered = (data: {
  constructive?: string;
  subConstructive?: string;
  subZone?: string;
  zone?: string;
}) => {
  return instance()
    .post('/dictionary/defectsByPositionFiltered', data)
    .then((response) => response.data);
};

export const getDefectsFilteredShort = (data: {
  constructive?: string;
  subConstructive?: string;
  subZone?: string;
  zone?: string;
}) => {
  return instance()
    .post('/dictionary/defectsByPositionFilteredShort', data)
    .then((response) => response.data);
};

export const getDefectType = (defectTypeId: number) => {
  return instance()
    .post<IDefectModel>('/defectType/load', { defectTypeId })
    .then((response) => response.data);
};

export const getZonesFiltered = (data: { constructive?: string; subConstructive?: string; subZone?: string }) => {
  return instance()
    .post('/defectType/zonesFiltered', data)
    .then((response) => response.data);
};

export const getSubZonesFiltered = (data: { constructive?: string; subConstructive?: string; zone?: string }) => {
  return instance()
    .post('/defectType/subZonesFiltered', data)
    .then((response) => response.data);
};

export const getConstructivesFiltered = (data: { subConstructive?: string; subZone?: string; zone?: string }) => {
  return instance()
    .post('/defectType/constructiveFiltered', data)
    .then((response) => response.data);
};

export const getSubConstructivesFiltered = (data: { constructive?: string; subZone?: string; zone?: string }) => {
  return instance()
    .post('/defectType/subConstructiveFiltered', data)
    .then((response) => response.data);
};

export const createApplication = (data: INewApplication) => {
  return instance()
    .post<IApplicationModel>('/application/v4/save', data)
    .then((response) => response.data);
};

export const getRemoteAppeals = () => {
  return instance()
    .post<IRemoteAppeal[]>('/dictionary/remoteAppealTypes', {})
    .then((response) => response.data);
};

export const getLevels = () => {
  return instance()
    .post<ICriticalLevel[]>('/checklist/level', {})
    .then((response) => response.data);
};

export const getCapexCriticalLevels = () => {
  return instance()
    .post<ICapexCriticalLevel[]>('/capexCriticalLevel/listAllByDate')
    .then((response) => response.data);
};

export const getUnits = () => {
  return instance()
    .post<IUnit[]>('/dictionary/unitDimension/list', {})
    .then((response) => response.data);
};

export const getPartners = (config: AxiosRequestConfig) => {
  return instance()
    .get<IPartner[]>('/ovksPartner/list', config)
    .then((response) => response.data);
};

export const getServiceTypes = () => {
  return instance()
    .get<IServiceType[]>('/serviceType/listAllNotDeleted')
    .then((response) => response.data);
};

export const getPositionsByUserId = (userId: string) => {
  return instance()
    .post<IPositionModel[]>('/users/listPositionsByUserId', { userId })
    .then((response) => response.data);
};

export const getDefectTypeWarrantyInfo = (data: { defectTypeId: number; realEstateGuid: string }) => {
  return instance()
    .post<{ onWarranty: boolean }>('/application/defectTypeWarrantyInfo', data)
    .then((response) => response.data);
};

export const checkIsPhoneRegisteredInBigApp = (phoneNumber: string) => {
  return instance()
    .post<IPhoneInfo>('/phoneNumber/isRegisteredInBigApp', { phoneNumber })
    .then((response) => response.data);
};

export const checkIsPhoneOfTenant = (phoneNumber: string) => {
  return instance()
    .post<IPhoneInfo>('/phoneNumber/isTenant', { phoneNumber })
    .then((response) => response.data);
};

export const getRoomDiscounts = (roomGuid: string) => {
  return instance()
    .post<IRoomDiscount[]>('/roomDiscount/getNotExpired', { roomGuid })
    .then((response) => response.data);
};

export const getRoomExpiredDiscounts = (roomGuid: string) => {
  return instance()
    .post<IRoomDiscount[]>('/roomDiscount/getExpired', { roomGuid })
    .then((response) => response.data);
};

export const loadRealEstateDefectType = (config?: AxiosRequestConfig) => {
  return instance()
    .get<IRealEstateDefectType>('/realEstateDefectType/load', config)
    .then((response) => response.data);
};

export const loadSmartRemont = (config?: AxiosRequestConfig) => {
  return instance()
    .get<ISmartRemont | null>('/smartRemont/load', config)
    .then((response) => response.data);
};

export const checkIfHasPublicApplications = (config?: AxiosRequestConfig) => {
  return instance()
    .get<{ hasApplicationIsPublic: boolean }>('/application/hasApplicationIsPublic', config)
    .then((response) => response.data);
};

export const getRealEstateRoomList = (config?: AxiosRequestConfig) => {
  return instance()
    .get<IRealEstateRoom[]>('/application/listRealEstateRoom', config)
    .then((response) => response.data);
};

export const getCountApplicationByUser = (userId: string) => {
  return instance()
    .post<ICountApplication>('/application/countApplicationByUser', { userId })
    .then((response) => response.data);
};

export const getCountApplicationByPosition = (data: { positionId: number; realEstateGuid: string }) => {
  return instance()
    .post<ICountApplication>('/application/countApplicationByPosition', data)
    .then((response) => response.data);
};

export const getCapexConstructiveList = () => {
  return instance()
    .post<ICapexConstructive[]>('/capex/constructive/listAllNotDeleted')
    .then((response) => response.data);
};

export const getPreviousApplications = (data: {
  applicationNumber?: string;
  realEstateGuid: string;
  roomGuid: string;
  pageNo: number;
  pageSize: number;
}) => {
  return instance()
    .post<{ list: IPreviousApplication[]; totalCount: number }>('/application/previousApplications', data)
    .then((response) => response.data);
};
